import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SpinnerCenter } from "@/components/Spinner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  getDefaultMenuCategories,
  createDefaultMenuCategory,
  deleteDefaultMenuCategory,
} from "@/lib/api/admin";
import type { DefaultMenuCategory } from "@/lib/api/types";

const DUPLICATE_ERROR = "A category with that name already exists";

export function DefaultMenuCategoriesPage() {
  const user = useAdminAuth();
  const qc = useQueryClient();

  const [newName, setNewName] = useState("");
  const [createError, setCreateError] = useState("");
  const [toDelete, setToDelete] = useState<DefaultMenuCategory | null>(null);

  const {
    data: categories = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["default-menu-categories"],
    queryFn: () => getDefaultMenuCategories(user!),
    enabled: !!user,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["default-menu-categories"] });

  const createMut = useMutation({
    mutationFn: () => createDefaultMenuCategory(user!, newName.trim()),
    onSuccess: () => {
      invalidate();
      setNewName("");
      setCreateError("");
      toast.success("Category created");
    },
    onError: (err: Error) => {
      if (err.message?.toLowerCase().includes("already exists")) {
        setCreateError(DUPLICATE_ERROR);
      } else {
        toast.error("Failed to create category");
      }
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteDefaultMenuCategory(user!, id),
    onSuccess: () => {
      invalidate();
      setToDelete(null);
      toast.success("Category deleted");
    },
    onError: () => {
      setToDelete(null);
      toast.error("Failed to delete category");
    },
  });

  function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    const duplicate = categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) {
      setCreateError(DUPLICATE_ERROR);
      return;
    }
    createMut.mutate();
  }

  if (isLoading) return <SpinnerCenter />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load categories</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h1 className="text-2xl font-bold">Default Menu Categories</h1>
        <p className="text-sm text-muted-foreground">
          Suggested category names shown to merchants when creating menu categories. Sorted
          alphabetically.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Categories ({categories.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <form onSubmit={submitCreate} className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (createError) setCreateError("");
                }}
                placeholder="New category name"
                maxLength={100}
                aria-invalid={!!createError}
                className="flex-1"
              />
              <Button type="submit" size="sm" disabled={!newName.trim() || createMut.isPending}>
                <Plus className="size-4 mr-1" />
                Add
              </Button>
            </form>
            {createError && <p className="text-xs text-destructive">{createError}</p>}
            <p
              className={`text-xs text-right ${
                newName.length > 100 ? "text-destructive" : "text-muted-foreground"
              }`}
            >
              {newName.length}/100
            </p>
          </div>

          <div className="space-y-1 min-h-[2rem]">
            {categories.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No categories yet.</p>
            )}
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2 rounded-md px-2 py-1.5">
                <span className="flex-1 text-sm">{cat.name}</span>
                <button
                  type="button"
                  onClick={() => setToDelete(cat)}
                  className="p-1 text-muted-foreground hover:text-destructive"
                  aria-label={`Delete ${cat.name}`}
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Delete category?"
        description={`"${toDelete?.name}" will be removed from the suggestions list.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate(toDelete!.id)}
      />
    </div>
  );
}
